import type { PostgrestError } from '@supabase/supabase-js'
import { AppError, type AppErrorCode, toAppError } from '@/shared/lib/appError'
import { isLikelyOfflineError } from '@/shared/lib/network'
import { Sentry } from '@/shared/lib/sentry'

const isPostgrestError = (value: unknown): value is PostgrestError =>
  Boolean(value) &&
  typeof value === 'object' &&
  'message' in (value as Record<string, unknown>) &&
  typeof (value as { message?: unknown }).message === 'string' &&
  ('code' in (value as Record<string, unknown>) ||
    typeof (value as { details?: unknown }).details === 'string' ||
    typeof (value as { hint?: unknown }).hint === 'string')

const codeByPostgrest = (error: PostgrestError): AppErrorCode => {
  const c = error.code
  if (c === 'PGRST116') return 'NOT_FOUND'
  if (c === 'PGRST205') return 'NOT_CONFIGURED'
  /** Postgres: ON CONFLICT sem UNIQUE compatível (ex.: falta migration `active_ingredients_dcb_code_key`). */
  if (c === '42P10') return 'NOT_CONFIGURED'
  if (c === '23505') return 'CONFLICT'
  if (c === '23514') return 'VALIDATION'
  if (c === '42501') return 'FORBIDDEN'
  if (c === 'P0001') return 'FORBIDDEN'
  return 'UNKNOWN'
}

const isMissingTableInSchemaCache = (error: PostgrestError): boolean =>
  error.code === 'PGRST205' ||
  /schema cache/i.test(error.message) ||
  /could not find the table/i.test(error.message)

const normalizeErrorMessage = (value: unknown): string =>
  typeof value === 'string' ? value.toLowerCase() : ''

/** Corpo PostgREST/Postgres por vezes vem como JSON string; extrai `message`/`error` legível. */
const tryParseNestedErrorMessage = (raw: string): string | null => {
  const t = raw.trim()
  if (!t.startsWith('{')) return null
  try {
    const j = JSON.parse(t) as Record<string, unknown>
    const inner =
      (typeof j.message === 'string' && j.message.trim()) ||
      (typeof j.error === 'string' && j.error.trim()) ||
      (typeof j.details === 'string' && j.details.trim())
    return inner || null
  } catch {
    return null
  }
}

/** JSON de erro cortado (ex.: proxy) — evita mostrar `{"` ao utilizador. */
const isLikelyTruncatedJsonMessage = (m: string): boolean => {
  const t = m.trim()
  return t.startsWith('{') && !t.includes('}') && t.length < 40
}

/** PostgREST devolve objetos com `code`; mensagens de falha de transporte vêm como texto de TypeError. */
const isLikelyTransportLayerPostgrestMessage = (rawMsg: string): boolean => {
  const m = normalizeErrorMessage(rawMsg)
  return (
    m.includes('failed to fetch') ||
    m.includes('networkerror') ||
    m.includes('load failed') ||
    m.includes('network request failed')
  )
}

const isLockTimeoutError = (error: unknown): boolean => {
  const lockErr = error as { isAcquireTimeout?: boolean }
  const errMsg = normalizeErrorMessage(error instanceof Error ? error.message : String(error ?? ''))
  return (
    lockErr?.isAcquireTimeout === true ||
    (errMsg.includes('lockmanager') && errMsg.includes('timed out'))
  )
}

const isAuthLikeMessage = (message: string): boolean => {
  const normalized = normalizeErrorMessage(message)
  if (!normalized) return false

  if (
    normalized.includes('auth is missing') ||
    normalized.includes('auth session is missing') ||
    normalized.includes('auth session missing') ||
    normalized.includes('missing authorization') ||
    normalized.includes('missing authentication') ||
    normalized.includes('jwserror') ||
    normalized.includes('não autenticado') ||
    normalized.includes('nao autenticado') ||
    normalized.includes('não autorizado') ||
    normalized.includes('nao autorizado') ||
    normalized.includes('unauthorized')
  ) {
    return true
  }

  const hasAuthSubject =
    normalized.includes('auth') ||
    normalized.includes('session') ||
    normalized.includes('jwt') ||
    normalized.includes('token')
  const hasFailureSignal =
    normalized.includes('missing') ||
    normalized.includes('invalid') ||
    normalized.includes('expired') ||
    normalized.includes('not authenticated') ||
    normalized.includes('not authorized')

  return hasAuthSubject && hasFailureSignal
}

const isPostgrestAuthError = (error: PostgrestError, message: string): boolean => {
  if (error.code === 'PGRST301' || error.code === 'PGRST302' || error.code === 'PGRST303') {
    return true
  }

  if (error.code === '42501' && isAuthLikeMessage(message)) {
    return true
  }

  return isAuthLikeMessage(message)
}

const EXPECTED_CODES: ReadonlySet<AppErrorCode> = new Set([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION',
  'CONFLICT',
  'NETWORK',
  'NOT_CONFIGURED',
])

/**
 * Códigos operacionais (auth, rede, RLS, schema) não vão ao Sentry.
 * Antes, NOT_CONFIGURED/FORBIDDEN com status 500 (fallback) ainda eram enviados por `status >= 500`.
 */
const shouldReportToSentry = (code: AppErrorCode): boolean => !EXPECTED_CODES.has(code)

const addPostgrestPermissionBreadcrumb = (error: PostgrestError, status: number): void => {
  if (error.code !== '42501') return

  Sentry.addBreadcrumb?.({
    category: 'supabase.permission',
    level: 'warning',
    message: 'PostgREST permission denied',
    data: {
      code: error.code,
      status,
      details: error.details,
      hint: error.hint,
    },
  })
}

export const toRepositoryError = (
  error: unknown,
  fallbackMessage = 'Falha no repositório.',
  fallbackStatus = 500,
): AppError => {
  if (error instanceof AppError) {
    if (shouldReportToSentry(error.code)) {
      Sentry.captureException(error, { tags: { layer: 'repository' } })
    }
    return error
  }

  // Auth/session antes de "offline": em ambientes de teste ou com rede instável,
  // navigator.onLine pode ser false e mascarar AuthSessionMissingError como NETWORK.
  const earlyErrMsg = normalizeErrorMessage(error instanceof Error ? error.message : String(error ?? ''))
  if (isAuthLikeMessage(earlyErrMsg)) {
    return new AppError(
      'Sessão expirada ou inválida. Faça login novamente.',
      { status: 401, code: 'UNAUTHORIZED', cause: error },
    )
  }

  if (isPostgrestError(error)) {
    const rawMsg = error.message || ''
    if (isLikelyTransportLayerPostgrestMessage(rawMsg)) {
      return new AppError('Falha na conexão. Verifique sua internet e tente novamente.', {
        status: 0,
        code: 'NETWORK',
        cause: error,
      })
    }
    if (isLikelyTruncatedJsonMessage(rawMsg)) {
      return new AppError(fallbackMessage, {
        status: 0,
        code: 'NETWORK',
        cause: error,
      })
    }
    const fromJson = tryParseNestedErrorMessage(rawMsg)
    const baseMsg =
      isMissingTableInSchemaCache(error)
        ? 'Supabase ainda não foi inicializado com as migrations obrigatórias (ex.: tabela profiles). Execute as migrations no projeto antes de continuar.'
        : (fromJson ?? rawMsg) || fallbackMessage

    const onConflictMissing =
      error.code === '42P10' ||
      /no unique or exclusion constraint matching the on conflict/i.test(baseMsg)

    const message = onConflictMissing
      ? 'Importação do catálogo DCB indisponível neste ambiente: falta a restrição UNIQUE em `dcb_code` na base de dados. O administrador deve aplicar as migrations Supabase (ficheiro `20260428100000_active_ingredients_dcb_code_unique_constraint.sql`, comando `supabase db push`).'
      : baseMsg

    const isAuthError = isPostgrestAuthError(error, message)
    const code = isAuthError
      ? 'UNAUTHORIZED'
      : onConflictMissing
        ? 'NOT_CONFIGURED'
        : codeByPostgrest(error)
    const status = (() => {
      if (isAuthError) return 401
      if (onConflictMissing) return 503
      if (isMissingTableInSchemaCache(error)) return 503
      if (error.code === '42501') return 403
      if (error.code === 'PGRST116') return 404
      if (error.code === '23505') return 409
      if (error.code === '23514') return 422
      return fallbackStatus
    })()

    const appError = new AppError(
      isAuthError ? 'Sessão expirada ou inválida. Faça login novamente.' : message,
      { status, code, cause: error },
    )

    addPostgrestPermissionBreadcrumb(error, status)

    if (shouldReportToSentry(code)) {
      Sentry.captureException(appError, {
        tags: { layer: 'repository', postgrestCode: error.code },
      })
    }

    return appError
  }

  if (isLikelyOfflineError(error)) {
    return new AppError('Falha na conexão. Verifique sua internet e tente novamente.', {
      status: 0,
      code: 'NETWORK',
      cause: error,
    })
  }

  if (isLockTimeoutError(error)) {
    return new AppError(
      'A sessão está ocupada. Feche outras abas do app ou aguarde alguns segundos e tente novamente.',
      { status: 503, code: 'UNKNOWN', cause: error },
    )
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    const rawMsg = (error as { message: string }).message
    if (isLikelyTruncatedJsonMessage(rawMsg)) {
      return new AppError(fallbackMessage, {
        status: 0,
        code: 'NETWORK',
        cause: error,
      })
    }
    const parsed = tryParseNestedErrorMessage(rawMsg)
    if (parsed && parsed.trim().length > 0) {
      const appError = new AppError(parsed.trim(), { status: fallbackStatus, code: 'UNKNOWN', cause: error })
      if (shouldReportToSentry('UNKNOWN')) {
        Sentry.captureException(appError, { tags: { layer: 'repository' } })
      }
      return appError
    }
  }

  const appError = toAppError(error, fallbackMessage, fallbackStatus)
  if (shouldReportToSentry(appError.code)) {
    Sentry.captureException(appError, { tags: { layer: 'repository' } })
  }
  return appError
}
